// ========== src/components/Modals/ChildSubscriptionModal.jsx ==========

import { useState, useEffect } from 'react';
import * as api from '../../utils/api';
import { PricingCard } from '../SubscriptionComponents';

const FEATURE_NAMES = {
    "basic_questions": "Paprasti klausimai",
    "hints": "Užuominos žaidimuose",
    "explanations": "Išsamūs paaiškinimai",
    "personalized": "Personalizuotos užduotys",
    "adaptive": "Adaptyvus sudėtingumas",
    "stories": "AI kuriamos istorijos",
    "priority": "Prioritetinis palaikymas",
    "all": "Visos funkcijos ir naujienos"
};

const ChildSubscriptionModal = ({ isOpen, onClose, child }) => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly', 'half_yearly', 'yearly'

    useEffect(() => {
        if (isOpen) {
            loadPlans();
        }
    }, [isOpen]);

    const loadPlans = async () => {
        setLoading(true);
        try {
            const data = await api.getPlans();
            if (data && data.plans) {
                setPlans(data.plans);
            }
        } catch (err) {
            console.error(err);
            setError("Nepavyko užkrauti planų.");
        } finally {
            setLoading(false);
        }
    };

    const handleSubscribe = async (tier) => {
        setLoading(true);
        setError(null);
        try {
            const checkoutSession = await api.createCheckout(tier, billingPeriod, child._id);
            if (checkoutSession && checkoutSession.url) {
                window.location.href = checkoutSession.url;
            } else {
                throw new Error("Nepavyko sukurti mokėjimo sesijos.");
            }
        } catch (err) {
            setError(err.message || 'Įvyko klaida bandant apmokėti.');
            setLoading(false);
        }
    };

    if (!isOpen || !child) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto p-8 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-3xl font-bold"
                >
                    ×
                </button>

                <div className="text-center mb-8">
                    <div className="text-6xl mb-4">{child.avatar || '👦'}</div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">
                        Prenumerata: {child.name}
                    </h2>
                    <p className="text-gray-600 text-lg mb-6">
                        Dabartinis planas: <span className="font-bold text-purple-600 uppercase">{child.plan || 'free'}</span>
                    </p>

                    {child.is_inherited && (
                        <div className="bg-green-100 text-green-700 p-4 rounded-2xl mb-8 flex items-center justify-center gap-3 border border-green-200">
                            <span className="text-2xl">👨‍👩‍👧‍👦</span>
                            <div className="text-left">
                                <p className="font-bold">Šis vaikas turi pilną prieigą!</p>
                                <p className="text-sm opacity-90">Vaikas naudojasi jūsų aukštesnio lygio Šeimos plano privalumais.</p>
                            </div>
                        </div>
                    )}

                    {/* Billing Period Toggle */}
                    <div className="flex justify-center mb-4">
                        <div className="bg-gray-100 p-1.5 rounded-2xl inline-flex items-center border border-gray-200">
                            {[
                                { id: 'monthly', label: 'Mėnesio' },
                                { id: 'half_yearly', label: '6 Mėnesiai', badge: '-15%' },
                                { id: 'yearly', label: 'Metų', badge: '-25%' }
                            ].map((period) => (
                                <button
                                    key={period.id}
                                    onClick={() => setBillingPeriod(period.id)}
                                    className={`
                                        relative px-6 py-2 rounded-xl transition-all duration-300 font-bold text-sm
                                        ${billingPeriod === period.id 
                                            ? 'bg-white text-purple-600 shadow-sm scale-105' 
                                            : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                                        }
                                    `}
                                >
                                    {period.label}
                                    {period.badge && (
                                        <span className={`
                                            absolute -top-3 -right-2 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider
                                            ${billingPeriod === period.id ? 'bg-green-500 text-white' : 'bg-purple-100 text-purple-600'}
                                        `}>
                                            {period.badge}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6 text-center">
                        {error}
                    </div>
                )}

                {loading && plans.length === 0 ? (
                    <div className="text-center py-10">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                        <p className="text-gray-600">Kraunami planai...</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {plans.map((plan) => (
                            <PricingCard
                                key={plan.tier}
                                plan={{
                                    ...plan,
                                    price: plan.prices?.[billingPeriod]?.price ?? 0,
                                    period: billingPeriod === 'monthly' ? 'mėn' : billingPeriod === 'half_yearly' ? '6 mėn' : 'metai',
                                    highlighted: plan.tier === 'premium'
                                }}
                                currentTier={child.plan || 'free'}
                                onSubscribe={handleSubscribe}
                                loading={loading}
                                featureNames={FEATURE_NAMES}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChildSubscriptionModal;
